import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Send, X, FileQuestion, User, MessageSquare, Loader2, Building2 } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import RFQItemsTable from '../components/ui/RFQItemsTable';
import { useToast } from '../hooks/useToast';
import {
    fetchPOSCustomers,
    fetchCatentriesUtil,
    fetchStoresWhereStaffAssigned,
    readTaxes,
    client,
    generateUniquePoNumber
} from '../apiHelper';

const NewRFQ = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [user, setUser] = useState(null);

    const [formData, setFormData] = useState({
        customerId: '',
        storeId: '',
        itemStoreId: '',
        rfqNumber: '',
        rfqDate: new Date().toISOString().slice(0, 16),
        subject: '',
        remarks: ''
    });

    const [lineItems, setLineItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dropdown options
    const [customerOptions, setCustomerOptions] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);
    const [itemOptions, setItemOptions] = useState([]);
    const [taxOptions, setTaxOptions] = useState([]);

    // Loading states
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingStores, setLoadingStores] = useState(true);
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadingTaxes, setLoadingTaxes] = useState(true);

    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) {
            setUser(JSON.parse(stored));
        } else {
            showToast('Please log in to create RFQs.', 'info');
            navigate('/login');
        }
    }, [showToast, navigate]);

    // Fetch Stores
    useEffect(() => {
        if (!user) return;
        const loadStores = async () => {
            setLoadingStores(true);
            try {
                const stores = await fetchStoresWhereStaffAssigned(user);
                if (stores && stores.length > 0) {
                    const mappedStores = stores.map(store => ({
                        label: store.identifier || `Branch ${store.store_id}`,
                        value: store.store_id
                    }));
                    setStoreOptions(mappedStores);
                    setFormData(prev => ({ 
                        ...prev, 
                        storeId: mappedStores[0].value, 
                        itemStoreId: mappedStores[0].value
                    }));
                }
            } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to load branches', 'error');
            }
            finally {
                setLoadingStores(false);
            }
        };
        loadStores();
    }, [user, showToast]);

    // Fetch Customers
    const fetchCustomers = useCallback(async (searchQuery = '') => {
        if (!user || !formData.storeId) return;

        setLoadingCustomers(true);
        try {
            const customers = await fetchPOSCustomers({ ...user, store_id: formData.storeId }, searchQuery);
            setCustomerOptions(customers.map(c => {
                const u = c.user || {};
                const fullName = `${u.first_name || ''} ${u.middle_name || ''} ${u.last_name || ''}`.trim() || 'Unnamed Customer';
                return {
                    label: `${fullName}`,
                    value: c.member_id
                };
            }));
        } catch (err) {
            showToast('Failed to load customers', 'error');
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, formData.storeId, showToast]);

    useEffect(() => {
        if (user && formData.storeId) {
            fetchCustomers();
        }
    }, [user, formData.storeId, fetchCustomers]);

    // Fetch Catalog Items
    useEffect(() => {
        if (!user) return;
        const loadItems = async () => {
            setLoadingItems(true);
            try {
                const items = await fetchCatentriesUtil(user);
                setItemOptions(items.map(item => ({
                    value: item.catentry_id,
                    label: item.productname,
                    price: parseFloat(item.listprice || 0)
                })));
            } catch (err) {
                // console.error(err);
                showToast('Failed to load catalog items', 'error');
            } finally {
                setLoadingItems(false);
            }
        };
        loadItems();
    }, [user, showToast]);

    // Fetch Taxes
    useEffect(() => {
        if (!user) return;
        const loadTaxes = async () => {
            setLoadingTaxes(true);
            try {
                const taxes = await readTaxes(user);
                setTaxOptions(taxes.map(tax => ({
                    label: `${tax.code || tax.identifier} (${parseFloat(tax.rate).toFixed(1)}%)`,
                    value: tax.taxcategory_id.toString(),
                    rate: parseFloat(tax.rate)
                })));
            } catch (err) {
                showToast('Failed to load tax categories', 'error');
            } finally {
                setLoadingTaxes(false);
            }
        };
        loadTaxes();
    }, [user, showToast]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        if (!formData.customerId) return 'Target Customer is required.';
        if (!formData.storeId) return 'Issuing Branch is required.';
        if (lineItems.length === 0) return 'Please add at least one item.';

        for (let i = 0; i < lineItems.length; i++) {
            if (!lineItems[i].itemId) return `Select an item for line ${i + 1}.`;
            if (parseFloat(lineItems[i].quantity || 0) <= 0) return `Quantity must be > 0 on line ${i + 1}.`;
        }
        return null;
    };

    const handleSave = async (status) => {
        const error = validateForm();
        if (error) {
            showToast(error, 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const itemsPayload = lineItems.map(item => {
                const selectedItem = itemOptions.find(opt => opt.value === item.itemId);
                const selectedTax = taxOptions.find(t => t.value === item.taxId);

                const price = parseFloat(item.targetPrice || selectedItem?.price || 0);
                const qty = parseFloat(item.quantity || 1);
                const subtotal = price * qty;
                const taxRate = (selectedTax?.rate || 0) / 100;
                const taxAmount = subtotal * taxRate;

                return {
                    catentry_id: Number(item.itemId),
                    quantity: qty,
                    subtotal: Number(subtotal.toFixed(2)),
                    tax_category_id: item.taxId ? Number(item.taxId) : null,
                    tax_amount: Number(taxAmount.toFixed(2)),
                    total_amount: Number((subtotal + taxAmount).toFixed(2))
                };
            });

            const subtotal = itemsPayload.reduce((sum, i) => sum + i.subtotal, 0);
            const tax_total = itemsPayload.reduce((sum, i) => sum + i.tax_amount, 0);

            const payload = {
                rfq_number: formData.rfqNumber || `RFQ-${generateUniquePoNumber()}`,
                member_id: user.users_id,
                customer_id: Number(formData.customerId),
                store_id: Number(formData.storeId),
                status: status,
                rfq_date: new Date(formData.rfqDate).toISOString(),
                subject: formData.subject,
                remarks: formData.remarks,
                subtotal: Number(subtotal.toFixed(2)),
                tax_total: Number(tax_total.toFixed(2)),
                grand_total: Number((subtotal + tax_total).toFixed(2)),
                items: itemsPayload
            };

            // console.log(payload)
            const response = await client.postData('/store/issue-rfq', payload);
            showToast(response.msg || 'RFQ created successfully!', 'success');
            navigate('/sales/quotation');

        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to create RFQ.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!user) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="animate-spin h-10 w-10 text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="w-full mx-auto p-4 md:p-8 max-w-7xl bg-zinc-50/30 min-h-screen">
            <div className="space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between pb-2">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-indigo-50 rounded-xl border border-indigo-100 text-indigo-600">
                            <FileQuestion size={22} />
                        </div>
                        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">
                            Create Request for Quote (RFQ)
                        </h1>
                    </div>
                </div>

                {/* Branch & Customer Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="p-6 border-zinc-200/60 shadow-sm rounded-xl bg-white space-y-4">
                        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
                            <Building2 size={16} className="text-zinc-400" />
                            <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-wider">Store Branch</h3>
                        </div>
                        <SearchableSelectBox
                            name="storeId"
                            label="Branch"
                            required
                            value={formData.storeId}
                            onChange={handleInputChange}
                            options={storeOptions}
                            loading={loadingStores}
                        />
                        <Input
                            name="rfqNumber"
                            label="RFQ Number"
                            placeholder="Auto-generated if left blank"
                            value={formData.rfqNumber}
                            onChange={handleInputChange}
                        />
                    </Card>

                    <Card className="p-6 border-zinc-200/60 shadow-sm rounded-xl bg-white space-y-4">
                        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
                            <User size={16} className="text-zinc-400" />
                            <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-wider">Customer Details</h3>
                        </div>
                        <SearchableSelectBox
                            name="customerId"
                            label="Customer"
                            required
                            value={formData.customerId}
                            onChange={handleInputChange}
                            options={customerOptions}
                            onSearch={fetchCustomers}
                            loading={loadingCustomers}
                        />
                        <Input
                            type="datetime-local"
                            name="rfqDate"
                            label="RFQ Date"
                            required
                            value={formData.rfqDate}
                            onChange={handleInputChange}
                        />
                    </Card>
                </div>

                {/* Additional Metadata */}
                <Card className="p-6 border-zinc-200/60 shadow-sm rounded-xl bg-white space-y-4">
                    <Input
                        name="subject"
                        required
                        label="Subject / Project Reference"
                        placeholder="e.g. Annual Supply Contract Quote"
                        value={formData.subject}
                        onChange={handleInputChange}
                    />
                    <Input
                        name="remarks"
                        label="Notes / Terms"
                        placeholder="Payment terms, delivery schedules, or quote conditions..."
                        value={formData.remarks}
                        onChange={handleInputChange}
                    />
                </Card>

                {/* Items Table Component */}
                <div className="border border-zinc-200/60 shadow-xs rounded-xl bg-white overflow-hidden">
                    <RFQItemsTable
                        itemOptions={itemOptions}
                        taxOptions={taxOptions}
                        currencySymbol="₦"
                        onItemsChange={setLineItems}
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end items-center gap-3 pt-4 border-t">
                    <Button className="flex items-center justify-center" variant="secondary" onClick={() => navigate(-1)} disabled={isSubmitting}>
                        <X size={16} className="mr-2" /> Cancel
                    </Button>
                    {/* <Button variant="secondary" onClick={() => handleSave('DRAFT')} disabled={isSubmitting}>
                        <Save size={16} className="mr-2" /> Save Draft
                    </Button> */}
                    <Button className="flex items-center justify-center" variant="primary" onClick={() => handleSave('SENT')} disabled={isSubmitting}>
                        <Send size={16} className="mr-2" /> Issue RFQ
                    </Button>
                </div>

            </div>
        </div>
    );
};

export default NewRFQ;